import { useEffect, useState } from 'react';
import { Table, Button, Group, FileInput, Stack, Text, Badge, LoadingOverlay, Box, Notification, Progress } from '@mantine/core';
import { IconUpload, IconDeviceFloppy } from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { selectImportedBookingsSelector } from '../../../redux/selectors/bookingSelector';
import { Booking, BookingType, BookingSource, BookingPaymentStatus } from '../../../models/booking.model';
import { importBookings } from '../../../redux/reducers/bookingSlice';
import { getAthensDate, getFormattedDate, getPaymentStatusColor, getShowTimeForDate } from '../../../utils/helper.utils';
import { selectIsAdminOrOwner } from '../../../redux/selectors/authSelector';
import { replace } from 'lodash';
import { DateTime } from 'luxon';
import Papa from 'papaparse';
import colors from 'utils/styles';

interface CSVBooking {
    short_id_seat: string;
    short_id: string;
    service_name: string;
    source: string;
    created_by: string;
    language_code: string;
    date_event: string;
    date_booking: string;
    buyer_email: string;
    tickets_count: string;
    total_price: string;
    currency: string;
    status: string;
    payment_gateway: string;
    name: string;
    email: string;
    phone: string;
    comments: string;
    country: string;
    hotel: string;
    ticket_type_count_json: string;
    custom_status: string;
    geo: string;
    order_list: string;
    deleted: number;
}

const ADULT_TICKET_ID = '11663';
const CHILDREN_TICKET_ID = '11664';

const mapSourceToBookingSource = (source: string): BookingSource => {
    const sourceMap: { [key: string]: BookingSource } = {
        'widget': BookingSource.WEBSITE,
        'backoffice': BookingSource.BACKOFFICE
    };

    return sourceMap[source.toLowerCase()] || source;
};

const mapStatusToPaymentStatus = (status: string): BookingPaymentStatus => {
    const statusMap: { [key: string]: BookingPaymentStatus } = {
        'confirmed': BookingPaymentStatus.CONFIRMED,
        'pending': BookingPaymentStatus.PENDING,
        'cancelled': BookingPaymentStatus.CANCELLED,
        'failed': BookingPaymentStatus.FAILED,
        'not paid': BookingPaymentStatus.NOT_PAID,
        'paid': BookingPaymentStatus.PAID,
        'partially paid': BookingPaymentStatus.PARTIALLY_PAID,
    };

    return statusMap[status.toLowerCase()] || status;
};

const ImportBookingsPage = () => {
    const dispatch = useDispatch();
    const [parsedBookings, setParsedBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const { importedBookings, loading: importedBookingsLoading, jobStatus } = useSelector(selectImportedBookingsSelector);
    const isAdminOrOwner = useSelector(selectIsAdminOrOwner);

    const transformCSVToBooking = (csvBooking: CSVBooking): Booking => {
        let childrenSeats = 0;
        let adultSeats = 0;
        try {
            const cleanJson = replace(csvBooking.ticket_type_count_json?.trim() || '{}', /\\"/g, '"');
            const ticketCounts = JSON.parse(cleanJson);
            childrenSeats = ticketCounts[CHILDREN_TICKET_ID] || 0;
            adultSeats = ticketCounts[ADULT_TICKET_ID] || 0;
        } catch (error) {
            console.error('error parsing ticket counts:', error);
            try {
                const orderList = csvBooking.order_list?.trim() || '';
                const adultMatch = orderList.match(/Adult\s+(\d+)/i);
                adultSeats = adultMatch ? parseInt(adultMatch[1]) : 0;
                const childMatch = orderList.match(/Child(?:ren)?\s+(\d+)/i);
                childrenSeats = childMatch ? parseInt(childMatch[1]) : 0;
            } catch (error) {
                console.error('error parsing order_list:', error);
                childrenSeats = 0;
                adultSeats = 0;
            }
        }

        // Create base date in Athens timezone and let Luxon handle DST
        const eventDate = getAthensDate(csvBooking.date_event?.trim(), false) as DateTime;
        // Get the show time based on the date
        const showTime = getShowTimeForDate(eventDate.toJSDate());
        const [hours, minutes] = showTime.split(':').map(Number);
        // Set the time in Athens timezone and let Luxon handle the DST conversion to UTC
        const athensTime = eventDate.set({ hour: hours, minute: minutes });
        // Convert to UTC by changing zone
        const bookingDate = athensTime.setZone('UTC').toJSDate();

        return {
            short_id: csvBooking.short_id?.trim(),
            show_id: csvBooking.service_name?.trim(), // Using service_name as show_id
            seat_ids: [],
            booked_adult_seats: adultSeats,
            booked_children_seats: childrenSeats,
            booked_total_seats: parseInt(csvBooking.tickets_count?.trim() || '0') || 0,
            booking_type: BookingType.STANDARD, // You might want to determine this based on some CSV field
            customer_name: csvBooking.name?.trim() || 'Unknown',
            customer_email: csvBooking.email?.trim() || csvBooking.buyer_email?.trim() || 'email not provided',
            customer_phone: csvBooking.phone?.trim() || 'phone not provided',
            booking_date: bookingDate,
            created_by: csvBooking.created_by?.trim() || 'system_import',
            created_source: mapSourceToBookingSource(csvBooking.source?.trim() || '') + '(Turitop)',
            language_code: csvBooking.language_code?.trim() || 'en',
            total_price: parseFloat(csvBooking.total_price?.trim() || '0') || 0,
            payment_status: mapStatusToPaymentStatus(csvBooking.status?.trim()?.toLowerCase() || ''),
            payment_type: csvBooking.payment_gateway?.trim(),
            booking_created_at: getAthensDate(csvBooking.date_booking?.trim()) as Date,
            booking_updated_at: getAthensDate() as Date,
            is_checked_in: false,
            notes: csvBooking.comments?.trim() || '' + ' - ' + csvBooking.custom_status?.trim() || '' + ' - ' + csvBooking.geo?.trim() || '' + ' - ' + csvBooking.order_list?.trim(),
        };
    };

    const handleFileUpload = (file: File | null) => {
        if (!file) return;
        setLoading(true);
        setError(null);
        setSuccess(null);

        Papa.parse(file, {
            header: true,
            delimiter: '\t', // Specify tab delimiter
            complete: (results: any) => {
                try {
                    const transformedBookings = results.data
                        .filter((row: any) => row.short_id && row.deleted?.trim() === '0')
                        .map((row: CSVBooking) => transformCSVToBooking(row));
                    setParsedBookings(transformedBookings);
                    setSuccess(`Successfully parsed ${transformedBookings.length} bookings`);
                } catch (err) {
                    setError('Error parsing CSV file: ' + (err as Error).message);
                } finally {
                    setLoading(false);
                }
            },
            error: (error: any) => {
                setError('Error parsing CSV file: ' + error.message);
                setLoading(false);
            }
        });
    };

    const handleSaveBookings = async () => {
        dispatch(importBookings(parsedBookings as any));
        setError(null);
        setSuccess(null);
        setParsedBookings([]);
    };

    useEffect(() => {
        if (importedBookings.length) {
            setSuccess(`Successfully imported ${importedBookings.length} bookings`);
        }
    }, [importedBookings]);

    const handleClear = () => {
        setParsedBookings([]);
        setError(null);
        setSuccess(null);
    };

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Text size="xl" fw={700}>Import Bookings</Text>
                <Group>
                    <FileInput
                        placeholder="Upload CSV file"
                        accept=".csv"
                        leftSection={<IconUpload size={16} />}
                        onChange={handleFileUpload}
                        disabled={!isAdminOrOwner}
                    />
                    <Button
                        variant="outline"
                        color="red"
                        onClick={handleClear}
                        disabled={parsedBookings.length === 0}
                    >
                        Clear
                    </Button>
                    <Button
                        leftSection={<IconDeviceFloppy size={16} />}
                        disabled={parsedBookings.length === 0 || !isAdminOrOwner}
                        onClick={handleSaveBookings}
                    >
                        Save {parsedBookings.length} Bookings
                    </Button>
                </Group>
            </Group>

            {error && (
                <Notification color="red" onClose={() => setError(null)} title="Error">
                    {error}
                </Notification>
            )}

            {success && (
                <Notification color="green" onClose={() => setSuccess(null)} title="Success">
                    {success}
                </Notification>
            )}

            {jobStatus.jobId && jobStatus.status === 'in_progress' && (
                <Box>
                    <Text size="sm" c="dimmed">Initializing imports: {jobStatus.progress.toFixed(1)}%</Text>
                    <Progress
                        value={jobStatus.progress}
                        color={jobStatus.status === 'failed' ? 'red' : 'blue'}
                        striped
                        animated
                    />
                    <Text size="xs" c="dimmed" mt={4}>
                        Processed {jobStatus.processed_items} of {jobStatus.total_items} items
                    </Text>
                </Box>
            )}
            <Box pos="relative">
                <LoadingOverlay
                    visible={loading || importedBookingsLoading}
                    overlayProps={{ radius: "md", blur: 0.5 }}
                    loaderProps={{ color: colors.appDarkBlue, type: 'bars' }}
                />

                <Table stickyHeader stickyHeaderOffset={60}>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>No.</Table.Th>
                            <Table.Th>Booking ID</Table.Th>
                            <Table.Th>Customer</Table.Th>
                            <Table.Th>Email</Table.Th>
                            <Table.Th>Phone</Table.Th>
                            <Table.Th>Event Date</Table.Th>
                            <Table.Th>Created Date</Table.Th>
                            <Table.Th>Seats</Table.Th>
                            <Table.Th>Total Price</Table.Th>
                            <Table.Th>Source</Table.Th>
                            <Table.Th>Status</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {parsedBookings.map((booking, index) => (
                            <Table.Tr key={index}>
                                <Table.Td>{index + 1}</Table.Td>
                                <Table.Td>{booking.short_id}</Table.Td>
                                <Table.Td>{booking.customer_name}</Table.Td>
                                <Table.Td>{booking.customer_email}</Table.Td>
                                <Table.Td>{booking.customer_phone}</Table.Td>
                                <Table.Td>
                                    {getFormattedDate(booking.booking_date)}
                                </Table.Td>
                                <Table.Td>
                                    {getFormattedDate(booking.booking_created_at)}
                                </Table.Td>
                                <Table.Td>{booking.booked_total_seats}</Table.Td>
                                <Table.Td>{booking.total_price}</Table.Td>
                                <Table.Td>
                                    <Badge color={booking.created_source === BookingSource.BACKOFFICE ? 'blue' : 'purple'}>{booking.created_source}</Badge>
                                </Table.Td>
                                <Table.Td>
                                    <Badge color={getPaymentStatusColor(booking.payment_status)}>
                                        {booking.payment_status}
                                    </Badge>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>

                {parsedBookings.length === 0 && (
                    <Text c="dimmed" ta="center" py="xl">
                        Upload a CSV file to see the preview of bookings here
                    </Text>
                )}
            </Box>
        </Stack>
    );
};

export default ImportBookingsPage;